import { useRef, useEffect, useCallback, useState } from 'react';
import {WSS_CHAT_CONN_URL} from '../config'

const MAX_RETRIES = 5; // Maximum number of reconnection attempts

export const useChatSocket = (setChatHistory, setStreaming, setIsThinking) => {
  const ws = useRef(null);
  const retryCount = useRef(0);
  const reconnectTimeout = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // Track connection status

  // Helper function to wait for the WebSocket to become open.
  const waitForConnection = useCallback((timeout = 5000, interval = 500) => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkConnection = () => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error("Timeout waiting for WebSocket connection."));
        } else {
          setTimeout(checkConnection, interval);
        }
      };
      checkConnection();
    });
  }, []);

  // Function to connect the WebSocket with retry logic.
  const connectWebSocket = useCallback(() => {
    console.log("Attempting to connect WebSocket... (Retry:", retryCount.current, ")");
    setConnectionStatus('connecting'); // Update connection status
    ws.current = new WebSocket(`${WSS_CHAT_CONN_URL}`);

    ws.current.onopen = () => {
      console.log("Connected to WebSocket server");
      setConnectionStatus('connected'); // Update connection status
      retryCount.current = 0; // Reset retry count on successful connection
    };

    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // If there's an error in the data
        if (data.error) {
          setChatHistory((prev) => [
            ...prev,
            { role: "error", text: data.error }
          ]);
          setStreaming(false);
          setIsThinking(false); // hide the "Thinking..." spinner on error
        }

        // If there's a data chunk (partial text from the AI)
        if (data.chunk) {
          // Hide the spinner on the *first* chunk arrival
          setIsThinking(false);

          setChatHistory((prev) => {
            const lastMessage = prev[prev.length - 1];
            // If the last message is an assistant chunk in progress, append
            if (lastMessage && lastMessage.role === "assistant" && !lastMessage.completed) {
              const updatedMessage = {
                ...lastMessage,
                text: lastMessage.text + data.chunk
              };
              return [...prev.slice(0, -1), updatedMessage];
            } else {
              // Otherwise, create a new assistant message
              return [...prev, { role: "assistant", text: data.chunk, completed: false }];
            }
          });
          // Don't setStreaming(false) here, so partial text can continue
        }

        // If we reach the end of the response
        if (data.end) {
          setChatHistory((prev) => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === "assistant") {
              const updatedMessage = { ...lastMessage, completed: true };
              return [...prev.slice(0, -1), updatedMessage];
            }
            return prev;
          });
          // Done streaming
          setStreaming(false);
          setIsThinking(false); // hide "Thinking..." in case it wasn't hidden yet
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setConnectionStatus('disconnected'); // Update connection status
      ws.current.close();
    };

    ws.current.onclose = (event) => {
      console.log("WebSocket disconnected:", event);
      setConnectionStatus('disconnected'); // Update connection status
      if (retryCount.current < MAX_RETRIES) {
        const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000);
        console.log(`Attempting to reconnect in ${delay} ms...`);
        reconnectTimeout.current = setTimeout(() => {
          retryCount.current += 1;
          connectWebSocket();
        }, delay);
      } else {
        console.error("Maximum reconnection attempts reached. Please check your connection or contact support.");
      }
    };
  }, [setChatHistory, setStreaming, setIsThinking]);

  // Initialize WebSocket connection on mount.
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (ws.current) {
        ws.current.close();
        ws.current = null; // Clean up WebSocket reference
      }
    };
  }, [connectWebSocket]);

  return { ws, connectWebSocket, waitForConnection, connectionStatus }; // Return connection status
};